/**
 * Domain parsing for the public free tools.
 *
 * Both free tools (AI Access Checker, Common Crawl Rank Checker) accept
 * anything domain-shaped a visitor might paste: a bare domain, a full URL with
 * a path and a query string, a "www." prefix, mixed case. They all have to
 * collapse to one canonical bare hostname so the same site never produces two
 * different lookups (and, from PR 2, two different per-domain URLs).
 *
 * This module is the single source of truth for that normalization. It is pure
 * (parse-domain only, no `@repo/database`, no `server-only`), so the browser
 * form, the API handler, and the server-rendered per-domain page all reject and
 * normalize exactly the same inputs.
 */

import { ParseResultType, fromUrl, parseDomain } from "parse-domain";
import { stripWwwFromDomain } from "./domain";

/** Why a pasted value is not usable as a public domain. */
export type PublicDomainFailure =
	| "empty"
	| "reserved"
	| "notListed"
	| "invalid";

export type ParsePublicDomainResult =
	| { ok: true; domain: string }
	| { ok: false; reason: PublicDomainFailure };

/**
 * Normalize user input to a canonical bare hostname.
 *
 * - `empty`: nothing but whitespace was entered.
 * - `reserved`: a reserved name such as `localhost` or `example` that has no
 *   public DNS presence, so there is nothing to look up.
 * - `notListed`: the TLD is not in the public suffix list (typos like
 *   `example.comm`, internal hostnames).
 * - `invalid`: not domain-shaped at all.
 *
 * @example
 * parsePublicDomain("https://www.Example.com/foo?x=1") // { ok: true, domain: "example.com" }
 * parsePublicDomain("blog.example.com")                // { ok: true, domain: "blog.example.com" }
 * parsePublicDomain("localhost")                       // { ok: false, reason: "reserved" }
 */
export function parsePublicDomain(input: string): ParsePublicDomainResult {
	const trimmed = input?.trim();
	if (!trimmed) {
		return { ok: false, reason: "empty" };
	}

	// fromUrl strips scheme, path, query, port and userinfo before parsing.
	const parseResult = parseDomain(fromUrl(trimmed));

	if (parseResult.type === ParseResultType.Listed) {
		const joined =
			`${parseResult.subDomains.join(".")}.${parseResult.domain}.${parseResult.topLevelDomains.join(".")}`.replace(
				/^\./,
				"",
			);
		// www.example.com and example.com serve the same site, so they share one
		// canonical form. Other subdomains are preserved.
		const domain = stripWwwFromDomain(joined.toLowerCase());
		return { ok: true, domain };
	}

	if (parseResult.type === ParseResultType.Reserved) {
		return { ok: false, reason: "reserved" };
	}

	if (parseResult.type === ParseResultType.NotListed) {
		return { ok: false, reason: "notListed" };
	}

	return { ok: false, reason: "invalid" };
}

/**
 * What a per-domain route should do with the `[domain]` path segment.
 *
 * - `ok`: the segment is already canonical, render it.
 * - `redirect`: the segment normalizes to a different domain, so the route
 *   should redirect to the canonical URL rather than serve duplicate content
 *   under two paths.
 * - `notFound`: the segment is not a usable public domain.
 */
export type DomainSegmentResolution =
	| { status: "ok"; domain: string }
	| { status: "redirect"; domain: string }
	| { status: "notFound" };

/**
 * Resolve a URL path segment to a canonical domain.
 *
 * @example
 * resolveDomainSegment("example.com")     // { status: "ok", domain: "example.com" }
 * resolveDomainSegment("WWW.Example.COM") // { status: "redirect", domain: "example.com" }
 * resolveDomainSegment("not-a-domain")    // { status: "notFound" }
 */
export function resolveDomainSegment(segment: string): DomainSegmentResolution {
	let decoded: string;
	try {
		decoded = decodeURIComponent(segment);
	} catch {
		// Malformed percent-encoding: never a real domain.
		return { status: "notFound" };
	}

	const parsed = parsePublicDomain(decoded);
	if (!parsed.ok) {
		return { status: "notFound" };
	}

	if (parsed.domain !== segment) {
		return { status: "redirect", domain: parsed.domain };
	}

	return { status: "ok", domain: parsed.domain };
}
